import { useEffect, useRef, useState } from 'react';

/**
 * Minimal scroll-reveal primitive.
 * Uses IntersectionObserver (native, zero dep). Triggers once.
 *
 * Usage:
 *   const [ref, visible] = useScrollReveal();
 *   <div ref={ref} className={visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} />
 */
export function useScrollReveal(options = {}) {
  const { threshold = 0.15, rootMargin = '0px 0px -80px 0px' } = options;
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Respect users who opted out of motion
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return [ref, visible];
}

/**
 * Wrapper that reveals children when they enter the viewport.
 * SPRINT 7 — vocabulaire élargi pour la narration publique (Motion V3 étendue) :
 *   effect="fade-up" (défaut) · "fade" · "scale" · "blur" (images éditoriales)
 * GPU uniquement (transform/opacity — filter réservé aux images hors LCP).
 * Optional `delay` (ms) for staggered reveals.
 */
const EFFECTS = {
  'fade-up': { hidden: 'opacity-0 translate-y-4', shown: 'opacity-100 translate-y-0' },
  fade: { hidden: 'opacity-0', shown: 'opacity-100' },
  scale: { hidden: 'opacity-0 scale-[0.96]', shown: 'opacity-100 scale-100' },
  blur: { hidden: 'opacity-0 blur-[6px] scale-[1.02]', shown: 'opacity-100 blur-0 scale-100' },
};

export function Reveal({ children, delay = 0, className = '', as: Tag = 'div', effect = 'fade-up' }) {
  const [ref, visible] = useScrollReveal();
  const fx = EFFECTS[effect] || EFFECTS['fade-up'];
  return (
    <Tag
      ref={ref}
      className={`transition [transition-duration:900ms] ease-out motion-reduce:transition-none ${
        visible ? fx.shown : fx.hidden
      } ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
    >
      {children}
    </Tag>
  );
}

/**
 * SPRINT 7 — MaskReveal : révélation « rideau » d'une ligne de titre.
 * Wrapper overflow-hidden + translateY(110%) → 0 : transform pur (GPU),
 * le motif signature des grandes maisons éditoriales.
 */
export function MaskReveal({ children, delay = 0, className = '', duration = 1100 }) {
  const [ref, visible] = useScrollReveal();
  return (
    <span ref={ref} className={`block overflow-hidden ${className}`}>
      <span
        className="block will-change-transform motion-reduce:transition-none motion-reduce:translate-y-0"
        style={{
          transform: visible ? 'translateY(0)' : 'translateY(110%)',
          transition: `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${visible ? delay : 0}ms`,
        }}
      >
        {children}
      </span>
    </span>
  );
}

/**
 * SPRINT 7 — Parallax légère (translateY GPU liée au scroll, rAF throttlé).
 * `speed` ∈ [-0.3, 0.3] recommandé. Désactivée si prefers-reduced-motion.
 */
export function Parallax({ children, speed = 0.12, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.bottom < -100 || r.top > vh + 100) return;
      const progress = (r.top + r.height / 2 - vh / 2) / vh;
      el.style.transform = `translate3d(0, ${(-progress * speed * 100).toFixed(2)}px, 0)`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [speed]);
  return <div ref={ref} className={`will-change-transform ${className}`}>{children}</div>;
}
